import _ from 'lodash'
import { Observable, interval, of, throwError, timer, zip } from 'rxjs'
import { catchError, concatMap, every, first, map, mergeMap, switchMap, tap } from 'rxjs/operators'

import { AssociatedMedias, AssociatedMediasResource, MEDIA_SUBJECTS_KEYS, PushCachedValueResult, Record, RecordLifecycleState, SearchResponse, SynchroMediaUpdates } from '@skinsoft/types'
import { SEARCH_TYPES } from '@skinsoft/const'
import { FailedToUpdateFirstMediaError } from './errors'
import { auditCampaignSelectors, recordSelectors } from '@skinsoft/selectors'
import { OfflineDataFetcher, RecordOfflineDataFetcher } from '@skinsoft/offline'
import { API_ENDPOINTS } from '@skinsoft/network'
import { mergeResource } from '@skinsoft/utils'
import { EntityDataStore } from '@skinsoft/entity-data-store'
import { ImageTransferHelper, MediaTransferHelper } from '@skinsoft/media-transfer-helper'
import { ItemDataStore } from '../item'

const NB_TRY_SYNC_UP = 15
const DELAY_TRY_SYNC_UP = 1000

export class RecordDataStore extends EntityDataStore<Record, RecordLifecycleState>{
    public removeOfflineData(id: string): void {
        this.offlineDataUpdateCacher.deleteBeforePushing(id)
    }

    public static getInstance() {
        if (!RecordDataStore.instance) {
            RecordDataStore.instance = new RecordDataStore();
        }

        return RecordDataStore.instance
    }

    public static install(Vue) {
        Vue.prototype.$recordDataStore = RecordDataStore.getInstance()
    }

    private static instance: RecordDataStore = null as any

    constructor() {
        super('recolement', [SEARCH_TYPES.auditRecord])
    }

    public exists(id: Record['id']) {
        return this.fetchOne(id)
            .pipe(first())
            .toPromise();
    }

    public async transitionRecord(recordId: string, lifecycleState: RecordLifecycleState, lifeCycleTranslations?: {[locale: string]: string}): Promise<Record> {
        const hasCachedUpdates = await this.hasCachedUpdates(recordId)
        
        if (window.navigator.onLine && !hasCachedUpdates) {
            return this.proxy
                .fetch(`/recolement`, { id: recordId, lifecycleState }, 'PATCH')
                .pipe(
                    concatMap(() => this.fetchOne(recordId, true)),
                    first(),
                )
                .toPromise();
        } else {
            return this.fetchOne(recordId).pipe(
                first(),
                switchMap(record => {
                    if(record.workflowSchema?.currentState?.name) {
                        _.set(record, 'workflowSchema.currentState.name', lifecycleState)
                    } else {
                        _.set(record, 'workflowSchema.currentState.value', lifecycleState)
                    }

                    if(lifeCycleTranslations) {
                        for(const [locale, value] of Object.entries(lifeCycleTranslations)) {
                            if(record.workflowSchema?.currentState?.[locale]) {
                                _.set(
                                    record,
                                    `workflowSchema.currentState.${locale}`,
                                    value
                                )
                            }
                        }
                    }
                    _.set(record, 'workflowSchema.currentState.availableLifecycleStates', this.getAvailableStatesFromState(lifecycleState))
                    return this.update(recordId, record)
                }),
                switchMap(() => this.fetchOne(recordId, true)
                ),
            ).toPromise()
        }
    }

    public fetchRandomRecord(offlineItem: boolean = false) {
        if (!window.navigator.onLine || offlineItem) {
            return this.offlineDataFetcher.fetchOneFromPredicate(data => true)
        } else {
            return throwError("This method is not meant to be called while having access to the network")
        }
    }

    public createOfflineRecordForCampaign(itemId: string, itemType: string, auditCampaignId: string): Observable<Record> {
        if(navigator.onLine && !OfflineDataFetcher.isTemporaryId(itemId)) {
            throwError("This method is not meant to be called while having access to the network")
        }

        const newRecord: Partial<Record> = {}

        // link item to recolement
        _.set(newRecord, 'recolementLinkSchema.linkedItem.linkableItem', itemId)
        _.set(newRecord, 'recolementLinkSchema.linkedItem.linkableItemReferenceType', itemType)

        // add a temporary field to store audit campaign
        _.set(newRecord, 'tempOfflineSchema.auditCampaign', auditCampaignId)

        // set status to inProgress
        _.set(newRecord, 'workflowSchema.currentState.value', RecordLifecycleState.IN_PROGRESS)

        return this.createOffline(newRecord)
    }

    protected initializeDataFetcher() {
        this.offlineDataFetcher = new RecordOfflineDataFetcher(this.sanitizedIdentifer);
    }

    private getAvailableStatesFromState(lifecycleState: RecordLifecycleState) {
        switch (lifecycleState) {
            case RecordLifecycleState.IN_PROGRESS:
                return [RecordLifecycleState.TO_VALIDATED, RecordLifecycleState.VALIDATED]
            case RecordLifecycleState.TO_VALIDATED:
                return [RecordLifecycleState.IN_PROGRESS, RecordLifecycleState.VALIDATED]
            case RecordLifecycleState.VALIDATED:
                return [RecordLifecycleState.IN_PROGRESS]
        }

    }

    public addMedia(recordId: string, mediaId: string) {
        return this.proxy.fetch(
            `/recolement/addMedia/${recordId}`,
            {idMedia: mediaId}, 
            'PUT'
        )
    }

    public updateFirstMedia(recordId: string, mediaId: string): Observable<Record> {
        return this.fetchOneOnlineMode(recordId).pipe(
            switchMap(record => {
                let associatedMedias: AssociatedMediasResource = {}
                if(record.associatedMedias) {
                    associatedMedias = record.associatedMedias
                }

                const updatedAssociatedMedias = MediaTransferHelper.permutateFirstMedia(mediaId, associatedMedias as AssociatedMedias)

                return this.update(recordId, {...record, associatedMedias: updatedAssociatedMedias})
            }),
            catchError(err => {
                return throwError(new FailedToUpdateFirstMediaError())
            }),
        )
    }

    protected pushCachedUpdate(dataToUpdate: SynchroMediaUpdates<Record>): Observable<PushCachedValueResult> {
        // The recolement doesn't changes AuditCampaign (at least not in reporter), 
        // thus we don't need to search the associated campaign

        const cachedChanges: Partial<Record> = this.convertDataToSyncUp(dataToUpdate.body)

        return this.fetchOneOnlineMode(dataToUpdate.id).pipe(
            switchMap(distantRecolement => {
                const updatedRecolement = mergeResource(distantRecolement, cachedChanges)
                
                let sanitizedDataToUpdate = _.cloneDeep(dataToUpdate)
                sanitizedDataToUpdate.body = updatedRecolement

                return super.pushCachedUpdate(sanitizedDataToUpdate)
            }),
            switchMap(res => {
                return this.applyAfterPushOperations(res, dataToUpdate)
            })
        )
    }

    protected pushCachedCreations(dataToCreate: SynchroMediaUpdates<Record>): Observable<PushCachedValueResult> {
        // We fetch the audit campaign containing the current recolement.
        // A recolement has already been created when we added the object to
        // the campaign (during push of audit campaign). We need to find which 
        // recolement in the audit campaign correspond to the same item id as 
        // the local recolement that we are "creating" and update it with the 
        // data we have locally
        
        const auditCampaignId = dataToCreate.body.tempOfflineSchema?.auditCampaign
        let itemId = recordSelectors.linkedItemId(dataToCreate.body)

        if(itemId && OfflineDataFetcher.isTemporaryId(itemId)) {
            itemId = ItemDataStore.getInstance().getUpdatedValue(itemId)
        }

        if(!auditCampaignId || !itemId) {
            return of({success: false})
        }

        let newId: string | null = null

        return of({}).pipe(
            switchMap(async nothing => {
                // we try fetching the auditCampaign + searching a recolement from the auditCampaign that match
                // the same item as our offline created recolement

                let nbTry = 0
                let totalSize = 0
                let recolementSearchResult: SearchResponse<Record>
                while(totalSize === 0 && nbTry < NB_TRY_SYNC_UP) {
                    const auditCampaign = await this.proxy.fetch(API_ENDPOINTS.auditProject + `/${auditCampaignId}`).toPromise()
                    
                    recolementSearchResult = await this.searchAll({
                        page: 0,
                        pageSize: 1,
                        types: ['Recolement'],
                        filteredIds: auditCampaignSelectors.auditRecords(auditCampaign),
                        advancedSearches: [{
                            leftCriteria: {
                                entityField: '5d5d3797bbffbb20bbcca5fa',// linked item
                                operator: 'equals',
                                type: 'criteria',
                                value: {
                                    completeSearch: false,
                                    refId: itemId,
                                    type: 'ref'
                                }
                            },
                            linkOperator: null,
                            rightCriteria: null,
                            type: 'requestSearch'
                        }],
                    }).toPromise()

                    totalSize = recolementSearchResult.totalSize
                    if(totalSize) {
                        return recolementSearchResult
                    }
                    nbTry++
                    await timer(DELAY_TRY_SYNC_UP).toPromise()
                }
                throw new Error('Unable to retrieve recolement')
            }),
            switchMap(recolementSearchResult => {
                newId = recolementSearchResult.allData[0].id
                this.synchronizationMap[dataToCreate.id] = newId
                return this.fetchOne(recolementSearchResult.allData[0].id)
            }),
            switchMap(distantRecolement => {
                const cachedChanges = this.convertDataToSyncUp(dataToCreate.body)
                const updatedRecolement = mergeResource(distantRecolement, cachedChanges)

                let sanitizedDataToUpdate = _.cloneDeep(dataToCreate)
                sanitizedDataToUpdate.body = updatedRecolement
                sanitizedDataToUpdate.id = distantRecolement.id

                // We use super.pushCachedUpdate event tough we are in pushCachedCreations because the recolement has 
                // already been created when we pushed the audit campaign changes (linked object field of audit campaign)
                return super.pushCachedUpdate(sanitizedDataToUpdate)
            }),
            catchError(err => {
                return of({ success: false })
            }),
            tap(res => {
                if(res.success) {
                    this.offlineDataUpdateCacher.removeCachedValue(dataToCreate.id)
                }
            }),
            switchMap(res => {
                return this.applyAfterPushOperations({...res, newId: newId ?? undefined}, dataToCreate)
            })
            // TO DO handle medias
        )        
    }

    private applyAfterPushOperations(pushResult: PushCachedValueResult, dataPushed: SynchroMediaUpdates<Record>): Observable<PushCachedValueResult> {
        return this.handleReplaySubjects(pushResult, dataPushed).pipe(
            tap(() => {
                try {
                    const newRecordId = this.synchronizationMap[dataPushed.id] ?? dataPushed.id
                    const recordState = recordSelectors.currenStateValue(dataPushed.body)
                    this.transitionRecord(newRecordId, recordState)
                } catch(err) {
                    
                }
            })
        )
    }

    private handleReplaySubjects(pushResult: PushCachedValueResult, dataToPush: SynchroMediaUpdates<Record>): Observable<PushCachedValueResult> {
        const recordId = pushResult.newId ?? dataToPush.id
        
        if(recordId) {
            const imageSubject = dataToPush.mediaSubjects[MEDIA_SUBJECTS_KEYS.image]

            if(imageSubject) {
                let newFirstMediaId: string | null = null

                const timer = interval(1000)

                // combination zip + timer = time output from another source
                return zip(imageSubject, timer).pipe(
                    map(([replaySubjectContent, timer]) => replaySubjectContent),
                    mergeMap((replaySubjectContent): Observable<PushCachedValueResult> => {
                        if(replaySubjectContent.newMediaId) {
                            return this.addMedia(recordId, replaySubjectContent.newMediaId).pipe(
                                tap(() => {
                                    ImageTransferHelper.getInstance().deleteImageInCache(replaySubjectContent.newMediaId as string)
                                    
                                    if(replaySubjectContent.cacheKeys) {
                                        for(const cacheKey of replaySubjectContent.cacheKeys) {
                                            if(dataToPush.cachedMedias?.first && cacheKey === dataToPush.cachedMedias.first) {
                                                newFirstMediaId = replaySubjectContent.newMediaId as string
                                            }

                                            ImageTransferHelper.getInstance().deleteImageInCache(cacheKey)
                                        }
                                    }
                                }),
                                tap(() => {
                                    ImageTransferHelper.getInstance().addImageToReporterSubAlbum(replaySubjectContent.newMediaId)
                                }),
                                switchMap(() => of({
                                    success: true
                                })),
                            )
                        }

                        return of({ success: false })
                    }),
                    every((result: PushCachedValueResult) => result.success),
                    switchMap(allSucceded => {
                        if(newFirstMediaId) {
                            this.updateFirstMedia(recordId, newFirstMediaId).subscribe()
                        }

                        return of({ success: allSucceded })
                    })
                )
            } else {
                return of(pushResult)
            }
        }

        return of({ success: false })
    }

    public convertDataToSyncUp(updatedRecord: any): Partial<Record> {
        let res: Partial<Record> = {}

        if(updatedRecord) {
            if(updatedRecord.recolementIdentificationSchema) {
                const recolementIdentificationSchema = updatedRecord.recolementIdentificationSchema

                if(recolementIdentificationSchema.numberIdentifier) {
                    if(recolementIdentificationSchema.numberIdentifier.value) {
                        _.set(
                            res,
                            'recolementIdentificationSchema.numberIdentifier',
                            recolementIdentificationSchema.numberIdentifier.value
                        )
                    } else {
                        _.set(
                            res,
                            'recolementIdentificationSchema.numberIdentifier',
                            recolementIdentificationSchema.numberIdentifier
                        )
                    }
                }

                if(recolementIdentificationSchema.recoleur) {
                    if(recolementIdentificationSchema.recoleur.length) {
                        for(let i=0; i<recolementIdentificationSchema.recoleur.length; i++) {
                            const recoleur = recolementIdentificationSchema.recoleur[i]

                            // alias ref
                            if(recoleur) {
                                if(recoleur.ref) {
                                    _.set(
                                        res,
                                        `recolementIdentificationSchema.recoleur[${i}]`,
                                        recoleur.ref
                                    )
                                } else {
                                    _.set(
                                        res,
                                        `recolementIdentificationSchema.recoleur[${i}]`,
                                        recoleur
                                    )
                                }
                            }
                        }
                    } else {
                        _.set(
                            res,
                            `recolementIdentificationSchema.recoleur`,
                            recolementIdentificationSchema.recoleur
                        )
                    }
                }

                // alias ref
                if(recolementIdentificationSchema.supervisor) {
                    if(recolementIdentificationSchema.supervisor.ref) {
                        _.set(
                            res,
                            `recolementIdentificationSchema.supervisor`,
                            recolementIdentificationSchema.supervisor.ref
                        )
                    } else {
                        _.set(
                            res,
                            `recolementIdentificationSchema.supervisor`,
                            recolementIdentificationSchema.supervisor
                        )
                    }
                }

                // TranslatedString
                if(recolementIdentificationSchema.context) {
                    _.set(
                        res,
                        `recolementIdentificationSchema.context`,
                        recolementIdentificationSchema.context
                    )
                }  
            }

            if(updatedRecord.recolementPropertyStatusSchema) {
                const recolementPropertyStatusSchema = updatedRecord.recolementPropertyStatusSchema
                
                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementPropertyStatusSchema, 'integrity')) {
                    _.set(
                        res,
                        `recolementPropertyStatusSchema.integrity`,
                        recolementPropertyStatusSchema.integrity
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementPropertyStatusSchema, 'deformationInstability')) {
                    _.set(
                        res,
                        `recolementPropertyStatusSchema.deformationInstability`,
                        recolementPropertyStatusSchema.deformationInstability
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementPropertyStatusSchema, 'tracesHumidity')) {
                    _.set(
                        res,
                        `recolementPropertyStatusSchema.tracesHumidity`,
                        recolementPropertyStatusSchema.tracesHumidity
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementPropertyStatusSchema, 'tracesInfestation')) {
                    _.set(
                        res,
                        `recolementPropertyStatusSchema.tracesInfestation`,
                        recolementPropertyStatusSchema.tracesInfestation
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementPropertyStatusSchema, 'highDust')) {
                    _.set(
                        res,
                        `recolementPropertyStatusSchema.highDust`,
                        recolementPropertyStatusSchema.highDust
                    )
                }

                // List complex
                if(recolementPropertyStatusSchema.commentOnSpecificPoints) {
                    if(recolementPropertyStatusSchema.commentOnSpecificPoints.length) {
                        for(let i=0; i<recolementPropertyStatusSchema.commentOnSpecificPoints.length; i++) {
                            const commentOnSpecificPoints = recolementPropertyStatusSchema.commentOnSpecificPoints[i]
    
                            if(commentOnSpecificPoints) {
                                // TranslatedString
                                if(commentOnSpecificPoints.commentary) {
                                    _.set(
                                        res,
                                        `recolementPropertyStatusSchema.commentOnSpecificPoints[${i}].commentary`,
                                        commentOnSpecificPoints.commentary
                                    )
                                }

                                // TranslatedString
                                if(commentOnSpecificPoints.typeOfCommentary) {
                                    _.set(
                                        res,
                                        `recolementPropertyStatusSchema.commentOnSpecificPoints[${i}].typeOfCommentary`,
                                        commentOnSpecificPoints.typeOfCommentary
                                    )
                                }
                            }
                        }
                    }
                    else {
                        _.set(
                            res,
                            `recolementPropertyStatusSchema.commentOnSpecificPoints`,
                            recolementPropertyStatusSchema.commentOnSpecificPoints
                        )
                    }
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementPropertyStatusSchema, 'stolen')) {
                    _.set(
                        res,
                        `recolementPropertyStatusSchema.stolen`,
                        recolementPropertyStatusSchema.stolen
                    )
                }
            }

            if(updatedRecord.recolementFollowUpActionSchema) {
                const recolementFollowUpActionSchema = updatedRecord.recolementFollowUpActionSchema

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementFollowUpActionSchema, 'existDocPhotography')) {
                    _.set(
                        res,
                        `recolementFollowUpActionSchema.existDocPhotography`,
                        recolementFollowUpActionSchema.existDocPhotography
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementFollowUpActionSchema, 'photographyInRecolement')) {
                    _.set(
                        res,
                        `recolementFollowUpActionSchema.photographyInRecolement`,
                        recolementFollowUpActionSchema.photographyInRecolement
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementFollowUpActionSchema, 'marksPhotography')) {
                    _.set(
                        res,
                        `recolementFollowUpActionSchema.marksPhotography`,
                        recolementFollowUpActionSchema.marksPhotography
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementFollowUpActionSchema, 'identifierNumberPhotography')) {
                    _.set(
                        res,
                        `recolementFollowUpActionSchema.identifierNumberPhotography`,
                        recolementFollowUpActionSchema.identifierNumberPhotography
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementFollowUpActionSchema, 'inventoried')) {
                    _.set(
                        res,
                        `recolementFollowUpActionSchema.inventoried`,
                        recolementFollowUpActionSchema.inventoried
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementFollowUpActionSchema, 'marked')) {
                    _.set(
                        res,
                        `recolementFollowUpActionSchema.marked`,
                        recolementFollowUpActionSchema.marked
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementFollowUpActionSchema, 'photographed')) {
                    _.set(
                        res,
                        `recolementFollowUpActionSchema.photographed`,
                        recolementFollowUpActionSchema.photographed
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementFollowUpActionSchema, 'processed')) {
                    _.set(
                        res,
                        `recolementFollowUpActionSchema.processed`,
                        recolementFollowUpActionSchema.processed
                    )
                }

                // TranslatedString
                if(recolementFollowUpActionSchema.processingDetail) {
                    _.set(
                        res,
                        `recolementFollowUpActionSchema.processingDetail`,
                        recolementFollowUpActionSchema.processingDetail
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementFollowUpActionSchema, 'located')) {
                    _.set(
                        res,
                        `recolementFollowUpActionSchema.located`,
                        recolementFollowUpActionSchema.located
                    )
                }
            }

            if(updatedRecord.recolementCheckingSchema) {
                const recolementCheckingSchema = updatedRecord.recolementCheckingSchema
                
                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementCheckingSchema, 'stolen')) {
                    _.set(
                        res,
                        `recolementCheckingSchema.stolen`,
                        recolementCheckingSchema.stolen
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementCheckingSchema, 'localized')) {
                    _.set(
                        res,
                        `recolementCheckingSchema.localized`,
                        recolementCheckingSchema.localized
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementCheckingSchema, 'presenceInventoryNumber')) {
                    _.set(
                        res,
                        `recolementCheckingSchema.presenceInventoryNumber`,
                        recolementCheckingSchema.presenceInventoryNumber
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementCheckingSchema, 'identifiable')) {
                    _.set(
                        res,
                        `recolementCheckingSchema.identifiable`,
                        recolementCheckingSchema.identifiable
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementCheckingSchema, 'markedOnObject')) {
                    _.set(
                        res,
                        `recolementCheckingSchema.markedOnObject`,
                        recolementCheckingSchema.markedOnObject
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementCheckingSchema, 'markedOnLabel')) {
                    _.set(
                        res,
                        `recolementCheckingSchema.markedOnLabel`,
                        recolementCheckingSchema.markedOnLabel
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementCheckingSchema, 'markedOnSupport')) {
                    _.set(
                        res,
                        `recolementCheckingSchema.markedOnSupport`,
                        recolementCheckingSchema.markedOnSupport
                    )
                }

                // TranslatedString
                if(recolementCheckingSchema.supportCommentary) {
                    _.set(
                        res,
                        `recolementCheckingSchema.supportCommentary`,
                        recolementCheckingSchema.supportCommentary
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementCheckingSchema, 'otherNumber')) {
                    _.set(
                        res,
                        `recolementCheckingSchema.otherNumber`,
                        recolementCheckingSchema.otherNumber
                    )
                }

                // TranslatedString
                if(recolementCheckingSchema.otherNumberCommentary) {
                    _.set(
                        res,
                        `recolementCheckingSchema.otherNumberCommentary`,
                        recolementCheckingSchema.otherNumberCommentary
                    )
                }

                // @ts-ignore boolean need to upgrade typescript
                if(Object.hasOwn(recolementCheckingSchema, 'compliantRegister')) {
                    _.set(
                        res,
                        `recolementCheckingSchema.compliantRegister`,
                        recolementCheckingSchema.compliantRegister
                    )
                }

                // List complex
                if(recolementCheckingSchema.recolementSources) {
                    if(recolementCheckingSchema.recolementSources.length) {
                        for(let i=0; i<recolementCheckingSchema.recolementSources.length; i++) {
                            const recolementSource = recolementCheckingSchema.recolementSources[i]
    
                            if(recolementSource) {
                                // TranslatedString
                                if(recolementSource.typeOfSource) {
                                    _.set(
                                        res,
                                        `recolementCheckingSchema.recolementSources[${i}].typeOfSource`,
                                        recolementSource.typeOfSource
                                    )
                                }

                                // TranslatedString
                                if(recolementSource.name) {
                                    _.set(
                                        res,
                                        `recolementCheckingSchema.recolementSources[${i}].name`,
                                        recolementSource.name
                                    )
                                }

                                // TranslatedString
                                if(recolementSource.precision) {
                                    _.set(
                                        res,
                                        `recolementCheckingSchema.recolementSources[${i}].precision`,
                                        recolementSource.precision
                                    )
                                }
                            }
                        }
                    } else {
                        _.set(
                            res,
                            `recolementCheckingSchema.recolementSources`,
                            recolementCheckingSchema.recolementSources
                        )
                    }
                }
                
                // TranslatedString
                if(recolementCheckingSchema.commentaryCompliant) {
                    _.set(
                        res,
                        `recolementCheckingSchema.commentaryCompliant`,
                        recolementCheckingSchema.commentaryCompliant
                    )
                }

                // TranslatedString
                if(recolementCheckingSchema.generalStatus) {
                    _.set(
                        res,
                        `recolementCheckingSchema.generalStatus`,
                        recolementCheckingSchema.generalStatus
                    )
                }
            }

            /*if(updatedRecord.recolementLinkSchema) {

            }

            if(updatedRecord.workflowSchema) {

            }*/
        }

        return res
    }
}